import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Shield, Activity, AlertTriangle } from "lucide-react";
import { TrendIndicator } from "@/components/security/TrendIndicator";
import { useAgentIssues, useAgentStats } from "@/hooks/useAgentDetail";
import { safeFormatDistanceToNow } from "@/utils/dateUtils";
import type { AgentDetail, StatsTimeWindow } from "@/services/aiAgentsService";

interface OverviewTabProps {
  agent: AgentDetail;
  timeWindow: StatsTimeWindow;
}

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: "bg-red-100 text-red-800 border-red-200",
  HIGH: "bg-orange-100 text-orange-800 border-orange-200",
  MEDIUM: "bg-yellow-100 text-yellow-800 border-yellow-200",
  LOW: "bg-gray-100 text-gray-600 border-gray-200",
};

export function OverviewTab({ agent, timeWindow }: OverviewTabProps) {
  const navigate = useNavigate();

  const { data: stats, isLoading: statsLoading, isError: statsError } = useAgentStats(agent.id, parseInt(timeWindow));
  const { data: issuesResponse, isLoading: issuesLoading, isError: issuesError } = useAgentIssues(agent.id, {
    items_per_page: 5,
  });

  const recentIssues = issuesResponse?.data ?? [];
  const totalIssues = stats?.total_issues ?? issuesResponse?.total_count ?? agent.issue_count;
  const openCount = (stats?.status_counts.open ?? 0) + (stats?.status_counts.in_review ?? 0);
  const critHighCount = (stats?.severity_counts.critical ?? 0) + (stats?.severity_counts.high ?? 0);

  return (
    <div className="space-y-6 pt-6">
      {/* KPI strip — 4 compact stat cards */}
      {statsError ? (
        <Card className="rounded-[15px] shadow-none">
          <CardContent className="py-8 text-center text-destructive">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-60" />
            <p className="text-sm">Failed to load agent statistics.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Total Issues */}
          <Card className="rounded-[15px] shadow-none">
            <CardContent className="pt-5 pb-4">
              {statsLoading ? (
                <Skeleton className="h-14 w-full" />
              ) : (
                <>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Total Issues</div>
                  <div className="flex items-end justify-between mt-1">
                    <div className="text-3xl font-bold">{totalIssues}</div>
                    {stats && Math.abs(stats.total_issues_trend_percentage) > 0 && (
                      <TrendIndicator
                        direction={stats.total_issues_trend_direction}
                        percentage={stats.total_issues_trend_percentage}
                        size="sm"
                      />
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    vs previous {timeWindow} days
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Open + In Review */}
          <Card className="rounded-[15px] shadow-none">
            <CardContent className="pt-5 pb-4">
              {statsLoading ? (
                <Skeleton className="h-14 w-full" />
              ) : (
                <>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Needs Attention</div>
                  <div className="flex items-end justify-between mt-1">
                    <div className="text-3xl font-bold">{openCount}</div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    open &amp; in review
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Critical + High */}
          <Card className="rounded-[15px] shadow-none">
            <CardContent className="pt-5 pb-4">
              {statsLoading ? (
                <Skeleton className="h-14 w-full" />
              ) : (
                <>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Critical &amp; High</div>
                  <div className="flex items-end justify-between mt-1">
                    <div className="text-3xl font-bold">{critHighCount}</div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    in current period
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Avg Risk Score */}
          <Card className="rounded-[15px] shadow-none">
            <CardContent className="pt-5 pb-4">
              {statsLoading ? (
                <Skeleton className="h-14 w-full" />
              ) : (
                <>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Risk Score</div>
                  <div className="flex items-end justify-between mt-1">
                    <div className="text-3xl font-bold">
                      {stats?.avg_risk_score ? stats.avg_risk_score.toFixed(2) : "0.00"}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {stats?.traces.total ?? agent.trace_count} traces analyzed
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent security issues */}
      <Card className="rounded-[15px] shadow-none">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Recent Security Issues
          </CardTitle>
        </CardHeader>
        <CardContent>
          {issuesError && (
            <div className="text-center py-8 text-destructive">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-60" />
              <p className="text-sm">Failed to load recent issues.</p>
            </div>
          )}
          {issuesLoading && !issuesError && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full rounded-[10px]" />
              ))}
            </div>
          )}
          {!issuesLoading && !issuesError && recentIssues.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Shield className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No security issues found for this agent.</p>
            </div>
          )}
          {!issuesLoading && !issuesError && recentIssues.length > 0 && (
            <div className="space-y-2">
              {recentIssues.map((issue) => (
                <div
                  key={issue.id}
                  className="flex cursor-pointer items-center gap-3 rounded-[10px] border p-3 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => navigate(`/issues/SEC-${String(issue.id).padStart(3, "0")}`)}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") navigate(`/issues/SEC-${String(issue.id).padStart(3, "0")}`);
                  }}
                >
                  <Badge className={`rounded-[5px] text-xs ${SEVERITY_COLORS[issue.severity] ?? ""}`}>
                    {issue.severity}
                  </Badge>
                  <span className="text-sm font-medium flex-1 truncate">{issue.title}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {safeFormatDistanceToNow(issue.detected_at, { addSuffix: true })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
